import { IconCalendar } from "@tabler/icons-react"
import "../masters/productService/ProductServices.css"
import "./Reports.css"
import { useState, useRef, useEffect } from "react";
import useLocalStorageState from "use-local-storage-state";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UpgradePlan } from "../../pages/UpgradePlan";
import { ReportTable } from "./ReportModule/ReportTable";
import { ReportTableHeader } from "./ReportModule/ReportTableHeader";
import { ReportCard } from "./ReportModule/ReportCard";
import { CustomReport } from "./ReportModule/CustomReport";


// to generate the schema for the respective module and pass the fields down to offcanvas
export const generateModuleSchema = (filterName) => {
    const moduleFields = {
        leads: [
            "search",
            "is_followup",
            "date",
            "contact_id",
            "owner_id",
            "product_services",
            "state",
            "city",
            "lead_source_id",
            "lead_status",
            "pipeline_id",
            "pipeline_stage_id",
        ],
        deals: [
            "search",
            "is_followup",
            "owner_id",
            "date",
            "contact_id",
            "product_services",
            "lead_status",
            "pipeline_id",
            "pipeline_stage_id",
        ],
        contact: [
            "search",
            "is_followup",
            "date",
            "contact_id",
            "state",
            "city",
            "lead_source_id",
            "company_id",
            "owner_id",
        ],
        company: [
            "search",
            "is_followup",
            "date",
            "industry",
            "state",
            "city",
            "owner_id",
            "business_type",
        ],
        quotation: [
            "search",
            "is_followup",
            "date",
            "state",
            "city",
            "owner_id",
            "product_services",
            "approver_id",
        ],
        purchase_order: [
            "search",
            "is_followup",
            "date",
            "state",
            "city",
            "owner_id",
            "product_services",
            "approver_id",
        ],
        sales_order: [
            "search",
            "is_followup",
            "date",
            "state",
            "city",
            "owner_id",
            "product_services",
            "approver_id",
            "quotation_id",
        ],
    }

    const fields = moduleFields[filterName?.toLowerCase()]
    const generatedSchema = []

    if (fields?.includes("search")) {
        generatedSchema.push({
            id: `${filterName}-search`,
            label_name: "Search",
            label_type: "text",
            module_name: filterName,
        })
    }
    if (fields?.includes("is_followup")) {
        generatedSchema.push({
            id: `${filterName}-is_followup`,
            label_name: "date-type",
            label_type: "single-option",
            module_name: filterName,
            fieldName: "Date Type",
        })
    }
    if (fields?.includes("date")) {
        generatedSchema.push({
            id: `${filterName}-date`,
            label_name: "Date",
            label_type: "date range",
            module_name: filterName,
            fieldName: "Date",
        })
    }
    fields?.forEach((field) => {
        if (
            field?.toLowerCase() !== "search" &&
            field?.toLowerCase() !== "date" &&
            field?.toLowerCase() !== "is_followup"
        ) {
            generatedSchema.push({
                id: `${filterName}-${field}`,
                label_name: field,
                label_type: "single-option",
                module_name: filterName,
            })
        }
    })
    return generatedSchema
}

const ScrollToTop = ({ onClick, show }) => {
    if (!show) return null

    return (
        <button
            onClick={onClick}
            style={{
                position: "fixed",
                bottom: "2rem",
                right: "2rem",
                width: "48px",
                height: "48px",
                background: "#7a3a5e",
                color: "white",
                border: "none",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(122, 58, 94, 0.3)",
                transition: "all 0.3s ease",
                zIndex: 1000,
            }}
            onMoEnter={(e) => {
                e.target.style.background = "#5f2e4a"
                e.target.style.transform = "translateY(-2px)"
            }}
            onMouseLeave={(e) => {
                e.target.style.background = "#7a3a5e"
                e.target.style.transform = "translateY(0)"
            }}
        >
            <FontAwesomeIcon icon={faChevronUp} />
        </button>
    )
}

// main report component
export const Report = () => {
    const [activeFilter, setActiveFilter] = useState("All Reports")
    const [tableData, setTableData] = useState([])
    const [selectedModule, setSelectedModule] = useState("")
    const [tableTitle, setTableTitle] = useState("")
    const [showScrollTop, setShowScrollTop] = useState(false)
    const tableRef = useRef(null)
    const topRef = useRef(null)
    const [header, setHeader] = useState([])
    const [columns, setColumns] = useState([])
    const [vcolumns, setVColumns] = useState([])
    const [customer, _] = useLocalStorageState('customer')
    const permission = customer.customer_details?.permission
    const isReport = permission?.find(c => c.module_name === "reports")

    const filters = [
        "All Reports",
        "Leads",
        "Contact",
        "Company",
        "Deals",
        "Quotation",
        "Purchase Order",
        "Sales Order",
        // "Project",
    ]

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 300)
        }

        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    const handleScrollToTop = () => {
        topRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    // Generate standard reports for a specific filter
    const getStandardReports = (filterName) => [
        {
            key: `three-month-${filterName}`,
            icon: <IconCalendar stroke={1} />,
            title: `Last 3 Months ${filterName}`,
            description: `${filterName} Report From Last 3 Months`,
            module_name: filterName,
            duration: "t_month",
        },
        {
            key: `current-month-${filterName}`,
            icon: <IconCalendar stroke={1} />,
            title: `Current Month ${filterName}`,
            description: `${filterName} Report of Current Month`,
            module_name: filterName,
            duration: "c_month",
        },
        {
            key: `last-month-${filterName}`,
            icon: <IconCalendar stroke={1} />,
            title: `Last Month ${filterName}`,
            description: `${filterName} Report of Last Month`,
            module_name: filterName,
            duration: "l_month",
        },
    ]

    const renderReportsForFilter = (filterName) => {
        const standardReports = getStandardReports(filterName)

        return (
            <>
                {standardReports.map((report) => (
                    <ReportCard
                        key={report.key}
                        icon={report.icon}
                        title={report.title}
                        description={report.description}
                        module_name={report.module_name}
                        duration={report.duration}
                        setTableData={setTableData}
                        setSelectedModule={setSelectedModule}
                        setTableTitle={setTableTitle}
                        tableRef={tableRef}
                    />
                ))}
                <CustomReport
                    filterName={filterName !== "All Reports" ? filterName : ""}
                    setTableData={setTableData}
                    setSelectedModule={setSelectedModule}
                    setTableTitle={setTableTitle}
                    tableRef={tableRef}
                />
            </>
        )
    }

    const renderAllReportsSections = () => {
        const reportFilters = filters.filter((filter) => filter !== "All Reports")

        // Get all reports from all filters and flatten into one array
        const allReports = reportFilters.flatMap((filterName) =>
            renderReportsForFilter(filterName)
        )

        return <div className="reports-grid">{allReports}</div>
    }

    return (
        <div className="container-fluid p-3 product-container mb-5" ref={topRef}>
            <div style={{ width: "80%", paddingRight: "20px" }}>
                <h5 className="fw-semibold mb-1">Reports</h5>
                <p className="text-muted small">
                    Track your sales activity, leads and deals here.
                </p>
            </div>
            <div className="app">
                <div className="filter-tabs">
                    {filters.map((filter) => (
                        <button
                            key={filter}
                            className={`filter-tab  ${activeFilter === filter ? "active" : ""
                                }`}
                            onClick={() => {
                                setActiveFilter(filter)
                                setTableData([])
                                setSelectedModule("")
                                setTableTitle("")
                            }}
                        >
                            {filter}
                        </button>
                    ))}
                </div>

                {activeFilter === "All Reports" ? (
                    <div className="all-reports-container">
                        {renderAllReportsSections()}
                    </div>
                ) : (
                    <div className="reports-grid">
                        {renderReportsForFilter(activeFilter)}
                    </div>
                )}
            </div>

            <div className="d-flex justify-content-between align-items-center">
                <ReportTableHeader
                    tableTitle={tableTitle}
                    data={tableData}
                    columns={columns}
                    headers={header}
                    setHeader={setHeader}
                    setColumns={setColumns}
                    setVColumns={setVColumns}
                    vcolumns={vcolumns}
                    setTableData={setTableData}
                />
            </div>
            <div ref={tableRef} style={{ scrollMarginTop: "2rem" }}>
                <ReportTable data={tableData} headers={vcolumns} />
            </div>

            <ScrollToTop onClick={handleScrollToTop} show={showScrollTop} />
            {!isReport &&
                <UpgradePlan moduleName="Report" />
            }
        </div>
    )
}
